import IconArrowBack from '@assets/icons/big arrow left.svg?react';
import IconLock from '@assets/icons/lock.svg?react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import type React from 'react';
import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, useLocation, useNavigate } from 'react-router-dom';

import { CONFIGURATOR_ROLE_KEY } from '../components/admin/widgetsGrids/types';
import LayoutAdmin from '../components/layouts/LayoutAdmin';
import LayoutEmpty from '../components/layouts/LayoutEmpty';
import LayoutMain from '../components/layouts/LayoutMain';
import { useAuthContext } from '../contexts/AuthContext';
// ErrorPage stays eager: it is used as the router errorElement (error
// boundary fallback), where a suspending lazy component is undesirable.
import ErrorPage from '../pages/404';

const Cards = lazy(() => import('../pages/cards'));
const CardTimes = lazy(() => import('../pages/cardTimes'));
const Contracts = lazy(() => import('../pages/contracts'));
const ContractsDetails = lazy(() => import('../pages/contractsDetails'));
const Customers = lazy(() => import('../pages/customers'));
const Dashboard = lazy(() => import('../pages/dashboard'));
const DashboardWidgetPopout = lazy(
	() => import('../pages/dashboardWidgetPopout'),
);
const Orders = lazy(() => import('../pages/orders'));
const DestPoint = lazy(() => import('../pages/destPoint'));
const DestPointDetails = lazy(() => import('../pages/destPointDetails'));
const Drivers = lazy(() => import('../pages/drivers'));
const GoogleCallback = lazy(() => import('../pages/googleCallback'));
const GuestOrder = lazy(() => import('../pages/guestOrder'));
const Inventorization = lazy(() => import('../pages/inventorization'));
const Warehouse = lazy(() => import('../pages/warehouse'));
const Login = lazy(() => import('../pages/login'));
const MicrosoftCallback = lazy(() => import('../pages/microsoftCallback'));
const ResetPassword = lazy(() => import('../pages/reset-password'));
const Products = lazy(() => import('../pages/products'));
const ProductsGroups = lazy(() => import('../pages/productsGroups'));
const Results = lazy(() => import('../pages/results'));
const Spy = lazy(() => import('../pages/spy'));
const ReportsPage = lazy(() => import('../pages/reports'));
const ReportBuilderPage = lazy(() => import('../pages/reportBuilder'));
const AdminDynamicTables = lazy(() => import('../pages/adminDynamicTables'));
const AdminDynamicTableDesigner = lazy(
	() => import('../pages/adminDynamicTableDesigner'),
);
const DynamicTable = lazy(() => import('../pages/dynamicTable'));
const DynamicTables = lazy(() => import('../pages/dynamicTables'));
const Vehicles = lazy(() => import('../pages/vehicles'));
const RequestMessages = lazy(() => import('../pages/requestMessages'));
const AdminDashboard = lazy(() => import('../pages/admin/adminDashboard'));
const AdminHistory = lazy(() => import('../pages/admin/adminHistory'));
const AdminLicenses = lazy(() => import('../pages/admin/adminLicenses'));
const AdminUsers = lazy(() => import('../pages/admin/adminUsers'));
const AdminAccessGroups = lazy(
	() => import('../pages/admin/adminAccessGroups'),
);
const AdminAudit = lazy(() => import('../pages/admin/adminAudit'));
const AdminDevices = lazy(() => import('../pages/admin/adminDevices'));
const AdminTaskScheduler = lazy(
	() => import('../pages/admin/adminTaskScheduler'),
);
const AdminWidgetsGrids = lazy(
	() => import('../pages/admin/adminWidgetsGrids'),
);

type RouterType = {
	path: string;
	element: React.ReactNode;
	layout: React.ElementType;
	children?: RouterType[];
	requiresAuth: boolean;
	// Backend role key '{Resource}.{Action}' required to view the page. The
	// backend still authorizes every request; this stops role-less users from
	// landing on pages whose first fetch can only 403.
	role?: string;
};

export const router = [
	{
		path: '/',
		element: <Results />,
		layout: LayoutMain,
		requiresAuth: true,
		role: 'Results.GetAll',
	},
	{
		path: '/login',
		element: <Login />,
		layout: LayoutEmpty,
		requiresAuth: false,
	},
	{
		path: '/guest-order',
		element: <GuestOrder />,
		layout: LayoutEmpty,
		requiresAuth: false,
	},
	{
		path: '/reset-password',
		element: <ResetPassword />,
		layout: LayoutEmpty,
		requiresAuth: false,
	},
	{
		path: '/microsoft-callback',
		element: <MicrosoftCallback />,
		layout: LayoutEmpty,
		requiresAuth: false,
	},
	{
		path: '/google-callback',
		element: <GoogleCallback />,
		layout: LayoutEmpty,
		requiresAuth: false,
	},
	{
		path: '/results',
		layout: LayoutMain,
		element: <Results />,
		requiresAuth: true,
		role: 'Results.GetAll',
	},
	{
		path: '/customers',
		layout: LayoutMain,
		element: <Customers />,
		requiresAuth: true,
		role: 'Customers.GetAll',
	},
	{
		path: '/orders',
		layout: LayoutMain,
		element: <Orders />,
		requiresAuth: true,
		role: 'Orders.GetAll',
	},
	{
		path: '/drivers',
		layout: LayoutMain,
		element: <Drivers />,
		requiresAuth: true,
		role: 'Drivers.GetAll',
	},
	{
		path: '/vehicles',
		layout: LayoutMain,
		element: <Vehicles />,
		requiresAuth: true,
		role: 'Vehicles.GetAll',
	},
	{
		path: '/products',
		layout: LayoutMain,
		element: <Products />,
		requiresAuth: true,
		role: 'Products.GetAll',
	},
	{
		path: '/products/groups',
		layout: LayoutMain,
		element: <ProductsGroups />,
		requiresAuth: true,
		role: 'Products.GetAllGroups',
	},
	{
		path: '/contracts',
		layout: LayoutMain,
		element: <Contracts />,
		requiresAuth: true,
		role: 'Contracts.GetAll',
	},
	{
		path: '/contracts/details',
		layout: LayoutMain,
		element: <ContractsDetails />,
		requiresAuth: true,
		role: 'Contracts.GetDetailsAll',
	},
	{
		path: '/admin/reports',
		layout: LayoutAdmin,
		element: <ReportsPage />,
		requiresAuth: true,
		role: 'Reports.GetAll',
	},
	{
		path: '/admin/reports/new',
		layout: LayoutAdmin,
		element: <ReportBuilderPage />,
		requiresAuth: true,
		role: 'Reports.Create',
	},
	{
		path: '/admin/reports/:id/edit',
		layout: LayoutAdmin,
		element: <ReportBuilderPage />,
		requiresAuth: true,
		role: 'Reports.Update',
	},
	{
		path: '/admin/dynamic-tables',
		layout: LayoutAdmin,
		element: <AdminDynamicTables />,
		requiresAuth: true,
		role: 'DynamicTables.GetTables',
	},
	{
		path: '/admin/dynamic-tables/new',
		layout: LayoutAdmin,
		element: <AdminDynamicTableDesigner />,
		requiresAuth: true,
		role: 'DynamicTables.CreateTable',
	},
	{
		path: '/admin/dynamic-tables/:name',
		layout: LayoutAdmin,
		element: <AdminDynamicTableDesigner />,
		requiresAuth: true,
		role: 'DynamicTables.GetTable',
	},
	{
		path: '/dynamic-tables',
		layout: LayoutMain,
		element: <DynamicTables />,
		requiresAuth: true,
		role: 'DynamicTables.GetAll',
	},
	{
		path: '/dynamic-tables/:tableName',
		layout: LayoutMain,
		element: <DynamicTable />,
		requiresAuth: true,
		role: 'DynamicTables.GetAll',
	},
	{
		path: '/spy',
		layout: LayoutMain,
		element: <Spy />,
		requiresAuth: true,
		role: 'Spy.GetAll',
	},
	{
		path: '/requestMessages',
		layout: LayoutMain,
		element: <RequestMessages />,
		requiresAuth: true,
		role: 'RequestedMsg.GetAll',
	},
	{
		path: '/destPoint',
		layout: LayoutMain,
		element: <DestPoint />,
		requiresAuth: true,
		role: 'DestPoint.GetAll',
	},
	{
		path: '/destPoint/details',
		layout: LayoutMain,
		element: <DestPointDetails />,
		requiresAuth: true,
		role: 'DestPoint.GetAllDetails',
	},
	{
		path: '/cards',
		layout: LayoutMain,
		element: <Cards />,
		requiresAuth: true,
		role: 'Cards.GetAll',
	},
	{
		path: '/cards/cardtimes',
		layout: LayoutMain,
		element: <CardTimes />,
		requiresAuth: true,
		role: 'Cards.GetCardTimesAll',
	},
	{
		path: '/warehouse',
		layout: LayoutMain,
		element: <Warehouse />,
		requiresAuth: true,
		role: 'Warehouse.GetAll',
	},
	{
		path: '/warehouse/inventorization',
		layout: LayoutMain,
		element: <Inventorization />,
		requiresAuth: true,
		role: 'Inventorization.GetAll',
	},
	{
		path: '/dashboard',
		layout: LayoutMain,
		element: <Dashboard />,
		requiresAuth: true,
		role: 'Results.GetAll',
	},
	{
		// Same role as /dashboard: the pop-out button only exists there, and
		// widget config in localStorage outlives logout.
		path: '/dashboard/widget/:id',
		layout: LayoutEmpty,
		element: <DashboardWidgetPopout />,
		requiresAuth: true,
		role: 'Results.GetAll',
	},
	{
		path: '/admin/dashboard',
		layout: LayoutAdmin,
		element: <AdminDashboard />,
		requiresAuth: true,
	},
	{
		path: '/admin/licenses',
		layout: LayoutAdmin,
		element: <AdminLicenses />,
		requiresAuth: true,
		role: 'Licenses.GetAll',
	},
	{
		path: '/admin/history',
		layout: LayoutAdmin,
		element: <AdminHistory />,
		requiresAuth: true,
		role: 'History.GetAll',
	},
	{
		path: '/admin/users',
		layout: LayoutAdmin,
		element: <AdminUsers />,
		requiresAuth: true,
		role: 'Users.GetAll',
	},
	{
		path: '/admin/accessGroups',
		layout: LayoutAdmin,
		element: <AdminAccessGroups />,
		requiresAuth: true,
		role: 'AccessGroups.GetAll',
	},
	{
		path: '/admin/devices',
		layout: LayoutAdmin,
		element: <AdminDevices />,
		requiresAuth: true,
		role: 'IoT.GetAll',
	},
	{
		path: '/admin/audit',
		layout: LayoutAdmin,
		element: <AdminAudit />,
		requiresAuth: true,
		role: 'Audit.GetAll',
	},
	{
		path: '/admin/taskScheduler',
		layout: LayoutAdmin,
		element: <AdminTaskScheduler />,
		requiresAuth: true,
		role: 'TaskScheduler.GetAll',
	},
	{
		path: '/admin/widgets-grids',
		layout: LayoutAdmin,
		element: <AdminWidgetsGrids />,
		requiresAuth: true,
		role: CONFIGURATOR_ROLE_KEY,
	},
	// 404 page with MinimalLayout
	{
		path: '*',
		element: <ErrorPage />,
		layout: LayoutEmpty,
		requiresAuth: false,
	},
];

// Shared loading indicator shown while auth initializes or a lazily
// loaded page chunk is being fetched.
const PageLoader = () => (
	<Box
		sx={{
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			height: '100vh',
		}}
	>
		<CircularProgress />
	</Box>
);

// Protected Route component

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
	const { isAuthenticated, isAuthInitializing } = useAuthContext();
	const location = useLocation();

	if (isAuthInitializing) {
		return <PageLoader />;
	}

	if (!isAuthenticated) {
		// Redirect to login if not authenticated

		return <Navigate to="/login" state={{ from: location }} replace />;
	}

	return children;
};

// Renders the page only when the user holds the route's backend role.
// Sits inside the layout so the shell (menu/header) stays visible and the
// content area shows the missing-role message instead.
const RoleGuard = ({
	role,
	children,
}: {
	role?: string;
	children: React.ReactNode;
}) => {
	const { hasRole } = useAuthContext();
	const { t } = useTranslation();
	const navigate = useNavigate();

	if (!role) {
		return children;
	}

	const [resource, action] = role.split('.');
	if (!resource || !action || hasRole(resource, action)) {
		return children;
	}

	return (
		<Box
			sx={{
				height: '100%',
				minHeight: '60vh',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				textAlign: 'center',
				gap: 2,
				p: '20px',
			}}
		>
			<Box
				sx={{
					width: 72,
					height: 72,
					borderRadius: '50%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					mb: 1,
					backgroundColor: (theme) => theme.palette.kemek.lightBlue,
					color: (theme) => theme.palette.kemek.blue,
				}}
			>
				<IconLock
					style={{ width: 32, height: 32, color: 'currentColor' }}
					aria-hidden
					focusable={false}
				/>
			</Box>
			<Typography sx={{ fontSize: 22, fontWeight: 600 }}>
				{t('global.noAccessTitle')}
			</Typography>
			<Typography
				sx={{
					fontSize: 14,
					fontWeight: 400,
					maxWidth: 420,
					lineHeight: 1.6,
					color: (theme) => theme.palette.kemek.darkGray,
				}}
			>
				{t('global.noAccessMessage')}
			</Typography>
			<Button
				variant="contained"
				color="primary"
				onClick={() => navigate(-1)}
				startIcon={<IconArrowBack style={{ width: 16, height: 16 }} />}
				sx={{ mt: 2, px: 3 }}
			>
				{t('global.back')}
			</Button>
			<Typography
				sx={{
					mt: 2,
					fontSize: 11,
					fontWeight: 400,
					color: (theme) => theme.palette.kemek.gray,
				}}
			>
				{t('global.noAccessPermission', { roleName: role })}
			</Typography>
		</Box>
	);
};

// Recursive function to generate routes

export const generateRoutes = (routesList: RouterType[]) =>
	routesList.map((route) => {
		const RouteLayout = route.layout;

		const RouteElement = route.element;

		if (route.children) {
			return (
				<Route
					key={route.path}
					path={route.path}
					errorElement={<ErrorPage />}
					element={
						route.requiresAuth ? (
							<ProtectedRoute>
								<RouteLayout>
									<RoleGuard role={route.role}>
										<Suspense fallback={<PageLoader />}>
											{RouteElement}
										</Suspense>
									</RoleGuard>
								</RouteLayout>
							</ProtectedRoute>
						) : (
							<RouteLayout>
								<Suspense fallback={<PageLoader />}>{RouteElement}</Suspense>
							</RouteLayout>
						)
					}
				>
					{generateRoutes(route.children)}
				</Route>
			);
		}

		return (
			<Route
				key={route.path}
				path={route.path}
				element={
					route.requiresAuth ? (
						<ProtectedRoute>
							<RouteLayout>
								<RoleGuard role={route.role}>
									<Suspense fallback={<PageLoader />}>{RouteElement}</Suspense>
								</RoleGuard>
							</RouteLayout>
						</ProtectedRoute>
					) : (
						<RouteLayout>
							<Suspense fallback={<PageLoader />}>{RouteElement}</Suspense>
						</RouteLayout>
					)
				}
			/>
		);
	});
